import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowUpRight } from "lucide-react";
import { listPosts } from "../../../lib/api";
import { categoryLabel } from "../../../lib/categories";

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}

export default function BlogsV2({ limit = 4, showHeader = true, category }) {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    listPosts({ category })
      .then((p) => setPosts(p.slice(0, limit)))
      .catch(() => setPosts([]));
  }, [limit, category]);

  if (!posts || posts.length === 0) return null;

  return (
    <section
      data-testid="v2-blogs"
      className="relative bg-white py-10 md:py-14"
    >
      <div className="container-x">
        {showHeader && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-7">
            <div className="max-w-2xl">
              <p className="overline text-[#3366FF]">News & blogs</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                Updates on business setup,{" "}
                <span className="font-black text-[#002366]">
                  auditing & accounting
                </span>
                .
              </h2>
            </div>
            <Link
              to="/blog"
              className="btn-ghost-navy text-sm self-start md:self-end"
            >
              View all articles
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {posts.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`v2-post-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group rounded-sm overflow-hidden border border-slate-200 bg-white hover:shadow-2xl hover:border-[#3366FF]/40 transition-all duration-500"
            >
              <Link to={`/blog/${p.slug}`} className="block">
                {p.cover_image && (
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#002366]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-slate-500 font-heading uppercase tracking-wider">
                    <Calendar className="w-3.5 h-3.5" />
                    {fmt(p.published_at || p.created_at)} · {categoryLabel(p.category)}
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-[#0F172A] tracking-tight text-lg leading-snug min-h-[56px]">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-[#3366FF]">
                    Read more
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
